import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSpinner,
  faPause,
  faSearch,
  faChevronLeft,
  faChevronRight,
  faUser,
  faClock,
  faClipboardList,
  faComment,
  faClipboardCheck
} from '@fortawesome/free-solid-svg-icons';
import api from '../../Api';

const formatHoldDate = (dateStr) => {
  if (!dateStr) return '–';
  const d = new Date(dateStr);
  return d.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const getRaisonLabel = (raison) => {
  const labels = {
    'metier': 'Metier',
    'projet': 'Projet',
    'metier_et_projet': 'Metier et Projet',
    'rien': 'Rien',
    'technique': 'Technique',
    'autre': 'Autre'
  };
  return labels[raison] || raison || '–';
};

const RespControleOnHold = () => {
  const navigate = useNavigate();
  const [checks, setChecks] = useState([]);
  const [surveys, setSurveys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [surveyFilter, setSurveyFilter] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
    per_page: 20,
    total: 0,
    from: null,
    to: null
  });

  const fetchOnHold = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.set('page', String(currentPage));
      params.set('per_page', '20');
      if (appliedSearch.trim()) params.set('search', appliedSearch.trim());
      if (surveyFilter) params.set('survey_id', surveyFilter);
      if (dateFilter) params.set('date', dateFilter);

      const response = await api.get(`/control/resp/on-hold?${params.toString()}`);
      const res = response?.data;

      if (res?.success) {
        const data = res.data;
        setChecks(Array.isArray(data?.data) ? data.data : []);
        setSurveys(Array.isArray(res.surveys) ? res.surveys : []);
        setPagination({
          current_page: data?.current_page ?? 1,
          last_page: data?.last_page ?? 1,
          per_page: data?.per_page ?? 20,
          total: data?.total ?? 0,
          from: data?.from ?? null,
          to: data?.to ?? null
        });
      }
    } catch (err) {
      console.error('Error fetching on-hold checks:', err);
      setChecks([]);
    } finally {
      setLoading(false);
    }
  }, [currentPage, appliedSearch, surveyFilter, dateFilter]);

  useEffect(() => {
    fetchOnHold();
  }, [fetchOnHold]);

  const handleSearch = () => {
    setAppliedSearch(searchInput);
    setCurrentPage(1);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSearch();
    }
  };

  const handleSurveyChange = (e) => {
    setSurveyFilter(e.target.value);
    setCurrentPage(1);
  };

  const handleDateFilterChange = (e) => {
    setDateFilter(e.target.value);
    setCurrentPage(1);
  };

  const resumeCheck = (check) => {
    navigate(`/resp-controle/on-hold/${check.id}`);
  };

  const total = pagination.total;
  const from = pagination.from;
  const to = pagination.to;
  const lastPage = pagination.last_page;
  const currentPageNum = pagination.current_page;

  return (
    <div className="min-h-screen bg-gray-50 relative overflow-hidden">
      <div className="absolute inset-0 opacity-10" style={{
        backgroundImage: 'radial-gradient(circle, #dc2626 1px, transparent 1px)',
        backgroundSize: '20px 20px'
      }} />
      <div className="relative z-10 px-3 py-4 sm:p-6">
        <div className="max-w-4xl mx-auto min-w-0">
          <div className="bg-white rounded-xl sm:rounded-2xl shadow-xl border border-gray-100 p-4 sm:p-6 mb-4 sm:mb-6">
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center gap-2">
              <FontAwesomeIcon icon={faPause} className="text-yellow-500 flex-shrink-0" />
              <span className="break-words">Mis en Attente (Resp. Controle)</span>
            </h1>
            <p className="text-sm sm:text-base text-gray-600 mt-1 mb-4 break-words">
              Soumissions que vous avez mises en attente. Cliquez sur une soumission pour reprendre le controle.
            </p>

            <div className="space-y-4">
              <div className="w-full min-w-0">
                <label className="block text-sm font-medium text-gray-700 mb-1">Rechercher</label>
                <div className="flex rounded-lg border border-gray-300 overflow-hidden focus-within:ring-2 focus-within:ring-red-500 focus-within:border-red-500 min-w-0">
                  <input
                    type="text"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="ID reponse, nom enqueteur ou titre enquete..."
                    className="flex-1 min-w-0 px-3 py-2.5 sm:py-2 border-0 focus:ring-0 text-gray-900 text-base"
                  />
                  <button
                    type="button"
                    onClick={handleSearch}
                    className="flex-shrink-0 px-4 py-2.5 sm:py-2 bg-red-600 text-white hover:bg-red-700 transition-colors"
                    title="Rechercher"
                  >
                    <FontAwesomeIcon icon={faSearch} />
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                <div className="w-full min-w-0">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Enquete</label>
                  <select
                    value={surveyFilter}
                    onChange={handleSurveyChange}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2.5 sm:py-2 focus:ring-2 focus:ring-red-500 focus:border-red-500 min-w-0 text-base"
                  >
                    <option value="">Toutes les enquetes</option>
                    {surveys.map((s) => (
                      <option key={s.id} value={s.id}>{s.title} ({s.limesurvey_id})</option>
                    ))}
                  </select>
                </div>
                <div className="w-full min-w-0">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date de mise en attente</label>
                  <input
                    type="date"
                    value={dateFilter}
                    onChange={handleDateFilterChange}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2.5 sm:py-2 focus:ring-2 focus:ring-red-500 focus:border-red-500 min-w-0 text-base"
                  />
                </div>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <FontAwesomeIcon icon={faSpinner} className="text-4xl text-red-600 animate-spin" />
            </div>
          ) : checks.length === 0 ? (
            <div className="bg-white rounded-xl sm:rounded-2xl shadow-xl border border-gray-100 p-8 sm:p-12 text-center">
              <FontAwesomeIcon icon={faPause} className="text-4xl sm:text-5xl text-gray-300 mb-4" />
              <p className="text-base sm:text-lg text-gray-600">Aucune soumission en attente</p>
              <p className="text-sm text-gray-500 mt-1 px-2">
                {appliedSearch || surveyFilter || dateFilter
                  ? 'Essayez d\'autres criteres de recherche ou de filtre.'
                  : 'Les soumissions que vous mettez en attente apparaitront ici.'}
              </p>
            </div>
          ) : (
            <>
              <div className="space-y-2 sm:space-y-3">
                {checks.map((check) => (
                  <button
                    key={check.id}
                    type="button"
                    onClick={() => resumeCheck(check)}
                    className="w-full text-left bg-white rounded-xl shadow border border-gray-100 p-3 sm:p-4 hover:border-yellow-300 hover:shadow-md transition-all min-w-0"
                  >
                    <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-start gap-2 sm:gap-4">
                      {/* Survey info */}
                      <div className="flex items-center gap-2 min-w-0 flex-shrink-0">
                        <FontAwesomeIcon icon={faClipboardList} className="text-gray-400 flex-shrink-0 w-4" />
                        <span className="font-medium text-gray-900 truncate text-sm sm:text-base">
                          {check.submission?.survey?.title || check.f2f_submission?.survey?.title || '–'}
                        </span>
                        {(check.submission?.survey?.limesurvey_id || check.f2f_submission?.survey?.limesurvey_id) && (
                          <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded">
                            {check.submission?.survey?.limesurvey_id || check.f2f_submission?.survey?.limesurvey_id}
                          </span>
                        )}
                      </div>

                      {/* Response ID */}
                      {(check.submission?.response_id || check.f2f_submission?.response_id) && (
                        <div className="flex items-center gap-1 pl-6 sm:pl-0">
                          <span className="text-xs text-blue-600 bg-blue-50 px-2 py-0.5 rounded font-medium">
                            ID: {check.submission?.response_id || check.f2f_submission?.response_id}
                          </span>
                        </div>
                      )}

                      {check.is_ai && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-indigo-100 text-indigo-700">
                          IA
                        </span>
                      )}

                      {/* Interviewer / Person name */}
                      <div className="flex items-center gap-2 min-w-0 flex-1 overflow-hidden pl-6 sm:pl-0">
                        <FontAwesomeIcon icon={faUser} className="text-gray-400 flex-shrink-0 w-4" />
                        <span className="text-gray-600 truncate text-sm sm:text-base">
                          {check.submission?.interviewer?.name || check.f2f_submission?.person?.name || '–'}
                        </span>
                      </div>
                    </div>

                    <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-2 sm:gap-4 mt-2 pl-6 sm:pl-0">
                      {/* Hold date */}
                      <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-500 flex-shrink-0">
                        <FontAwesomeIcon icon={faClock} className="flex-shrink-0 w-3.5 sm:w-4" />
                        <span>Mis en attente le: {formatHoldDate(check.checked_at)}</span>
                      </div>

                      {/* Reason badge */}
                      {check.raison && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                          {getRaisonLabel(check.raison)}
                        </span>
                      )}
                    </div>

                    {/* Comment preview */}
                    {check.comment && (
                      <div className="flex items-start gap-2 text-xs sm:text-sm text-gray-500 mt-2 pl-6 sm:pl-0">
                        <FontAwesomeIcon icon={faComment} className="flex-shrink-0 w-3.5 sm:w-4 mt-0.5" />
                        <span className="line-clamp-2">{check.comment}</span>
                      </div>
                    )}
                  </button>
                ))}
              </div>

              {total > 0 && (
                <div className="mt-4 sm:mt-6 flex flex-col sm:flex-row sm:flex-wrap sm:items-center sm:justify-between gap-3 bg-white rounded-xl border border-gray-100 p-3 sm:p-4">
                  <p className="text-xs sm:text-sm text-gray-600 text-center sm:text-left order-2 sm:order-1">
                    Affichage de <span className="font-medium">{from}</span> a <span className="font-medium">{to}</span> sur <span className="font-medium">{total}</span> en attente
                  </p>
                  <div className="flex items-center justify-center gap-2 order-1 sm:order-2">
                    <button
                      type="button"
                      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                      disabled={currentPageNum <= 1}
                      className="inline-flex items-center justify-center gap-1 px-3 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed text-sm min-w-[2.5rem]"
                    >
                      <FontAwesomeIcon icon={faChevronLeft} />
                      <span className="hidden sm:inline">Precedent</span>
                    </button>
                    <span className="text-xs sm:text-sm text-gray-600 px-2 whitespace-nowrap">
                      Page {currentPageNum} / {lastPage}
                    </span>
                    <button
                      type="button"
                      onClick={() => setCurrentPage((p) => Math.min(lastPage, p + 1))}
                      disabled={currentPageNum >= lastPage}
                      className="inline-flex items-center justify-center gap-1 px-3 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed text-sm min-w-[2.5rem]"
                    >
                      <span className="hidden sm:inline">Suivant</span>
                      <FontAwesomeIcon icon={faChevronRight} />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default RespControleOnHold;
